<script setup lang="ts">
/**
 * Changes waiting on you, and changes you are waiting on.
 *
 * A proposal nobody sees is a proposal nobody answers, and one that lapses
 * leaves the lesson exactly as it was — which is usually not what either party
 * wanted. So this leads with what is being asked and by when, rather than with
 * a tidy list of everything outstanding.
 */
import { ref } from 'vue'
import { Button } from '@/Components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card'
import { Clock, AlertCircle } from 'lucide-vue-next'
import { getLessonTimezone } from '@/lib/lessonTime'
import { sendPayingIfNeeded } from '@/lib/lessonPayment'

interface Proposal {
  id: number
  type: string
  type_label: string
  status: string
  message: string | null
  expires_at: string
  is_mine: boolean
  summary: string
  from_name: string
  subscription_id: number
}

const props = defineProps<{ proposals: Proposal[] }>()
const emit = defineEmits<{ changed: [] }>()

const busy = ref<number | null>(null)
const error = ref('')

async function answer(proposal: Proposal, approved: boolean) {
  busy.value = proposal.id
  error.value = ''

  try {
    await sendPayingIfNeeded(proposal.subscription_id, paymentIntentId =>
      window.axios.post(`/lesson-changes/proposals/${proposal.id}/answer`, { approved, payment_intent_id: paymentIntentId })
    )
    emit('changed')
  } catch (failure: any) {
    error.value = failure?.response?.data?.message ?? 'That did not go through. Please try again.'
  } finally {
    busy.value = null
  }
}

async function withdraw(proposal: Proposal) {
  busy.value = proposal.id
  error.value = ''

  try {
    await window.axios.delete(`/lesson-changes/proposals/${proposal.id}`)
    emit('changed')
  } catch (failure: any) {
    error.value = failure?.response?.data?.message ?? 'We could not withdraw that. Please try again.'
  } finally {
    busy.value = null
  }
}

function deadline(when: string): string {
  return new Date(when).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: getLessonTimezone(),
  })
}
</script>

<template>
  <Card v-if="props.proposals.length">
    <CardHeader>
      <CardTitle class="text-base">Changes to agree</CardTitle>
    </CardHeader>

    <CardContent class="space-y-3">
      <p v-if="error" class="flex items-start gap-2 text-sm text-red-700">
        <AlertCircle class="mt-0.5 h-4 w-4 shrink-0" />
        {{ error }}
      </p>

      <div
        v-for="proposal in props.proposals"
        :key="proposal.id"
        class="rounded-lg border border-gray-200 p-4"
      >
        <p class="text-sm font-medium text-gray-900">
          {{ proposal.is_mine ? 'You asked' : `${proposal.from_name} asked` }}: {{ proposal.type_label }}
        </p>
        <p class="mt-0.5 text-sm text-gray-600">{{ proposal.summary }}</p>
        <p v-if="proposal.message" class="mt-2 text-sm italic text-gray-500">“{{ proposal.message }}”</p>

        <p class="mt-2 flex items-center gap-1 text-xs text-gray-500">
          <Clock class="h-3 w-3" />
          Needs an answer by {{ deadline(proposal.expires_at) }} UK time
        </p>

        <!-- Until it is answered the lesson stands exactly as booked, and will
             be charged as booked. Saying so stops "I asked to move it" being
             heard as "so I did not owe for it". -->
        <p v-if="proposal.is_mine" class="mt-2 text-xs text-gray-500">
          Nothing changes until they agree. The lesson is still going ahead as booked.
        </p>

        <div class="mt-3 flex gap-2">
          <template v-if="proposal.is_mine">
            <Button variant="ghost" size="sm" :disabled="busy === proposal.id" @click="withdraw(proposal)">
              Withdraw
            </Button>
          </template>
          <template v-else>
            <Button size="sm" :disabled="busy === proposal.id" @click="answer(proposal, true)">
              {{ busy === proposal.id ? 'Saving…' : 'Agree' }}
            </Button>
            <Button variant="outline" size="sm" :disabled="busy === proposal.id" @click="answer(proposal, false)">
              Decline
            </Button>
          </template>
        </div>
      </div>
    </CardContent>
  </Card>
</template>
